/**
 * AdminPage — main admin panel.
 *
 * Action buttons (admin only): Stock Management · AI PDF Import · Storage.
 * Storage is a dialog (admin-only) showing the storage stats card on demand.
 * Tabs: Products / Bulk Upload / Staff (Staff is admin-only).
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import { useAuth, api } from '../lib/store';
import { Sparkles, Package, Database, FileText, MessageCircle, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';

import { ProductManagement } from '../components/admin/ProductManagement';
import { BulkUpload } from '../components/admin/BulkUpload';
import { UserManagement } from '../components/admin/UserManagement';
import StorageStatsCard from '../components/admin/StorageStatsCard';
import SettingsManagement from '../components/admin/SettingsManagement';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import CatalogHealthPage from './CatalogHealthPage';
import WhatsAppAdminPanel from '../components/admin/WhatsAppAdminPanel';
import WalkinRegisterPanel from '../components/admin/WalkinRegisterPanel';
import LeadRegisterPanel from '../components/admin/LeadRegisterPanel';
import ComplaintsPanel from '../components/admin/ComplaintsPanel';
import TodayDashboardPanel from '../components/admin/TodayDashboardPanel';
import ClientErrorsPanel from '../components/admin/ClientErrorsPanel';
import CollectionsAdmin from '../components/admin/CollectionsAdmin';
import ReportsPanel from '../components/admin/ReportsPanel';

export default function AdminPage() {
  const { isAdmin, isManager, canManageProducts } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showStorage, setShowStorage] = useState(false);

  // URL-driven tab selection. Lets the QuotationDetail "Back"
  // button return to Admin → Staff tab with the correct staff card
  // pre-expanded (see UserManagement's expandUser useEffect). Falls back
  // to the role-appropriate default when no tab param is present.
  const defaultTab = (isAdmin || isManager) ? 'today' : 'products';
  const urlTab = searchParams.get('tab');
  const activeTab = urlTab || defaultTab;
  const handleTabChange = (v) => {
    // Preserve any other query params (expandUser, etc.) — only replace
    // `tab`. Otherwise clicking a tab would drop the expandUser hint.
    const next = new URLSearchParams(searchParams);
    if (v === defaultTab && !next.has('expandUser')) {
      next.delete('tab');
    } else {
      next.set('tab', v);
    }
    setSearchParams(next, { replace: true });
  };

  // Download Roles & Permissions guide PDF — sharable with the staff team.
  const handleDownloadRolesGuide = async () => {
    try {
      const res = await api.downloadRolesGuide();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'HEDA_Plywoods_Roles_and_Permissions_Guide.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Roles guide downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download roles guide');
    }
  };

  // Download Pending Quotations report PDF — admin only, on-demand.
  const handleDownloadPendingReport = async () => {
    try {
      const res = await api.downloadPendingReport();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `HEDA_Pending_Quotations_${today}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Pending report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download pending report');
    }
  };

  if (!canManageProducts) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Product management access required</p>
      </div>
    );
  }

  // ─── Flat pill nav config ──────────────────────────────────────────
  // The panel exposes EIGHT top-level pills in this exact order:
  //   Analytics | Staff | Today | Walk-ins | Leads | Catalog | System | Collections
  // Six of them open a single tab directly (analytics, staff, today,
  // walkins, leads, collections). Catalog and System are groups —
  // clicking them reveals a second-row tab strip.
  //
  // Tab VALUES, data-testids, and role conditionals stay identical to
  // the previous grouped layout so `?tab=…` deep links keep working.
  const PILLS = [
    {
      key: 'analytics', label: 'Analytics',
      tabs: [{ value: 'analytics', canShow: isAdmin }],
    },
    {
      key: 'reports', label: 'Reports',
      tabs: [{ value: 'reports', canShow: isAdmin || isManager }],
    },
    {
      key: 'staff', label: 'Staff',
      tabs: [{ value: 'staff', canShow: isAdmin || isManager }],
    },
    {
      key: 'today', label: 'Today',
      tabs: [{ value: 'today', canShow: isAdmin || isManager }],
    },
    {
      key: 'walkins', label: 'Walk-ins',
      tabs: [{ value: 'walkins', canShow: isAdmin || isManager }],
    },
    {
      key: 'leads', label: 'Leads',
      tabs: [{ value: 'leads', canShow: isAdmin || isManager }],
    },
    {
      key: 'complaints', label: 'Complaints',
      tabs: [{ value: 'complaints', canShow: isAdmin || isManager }],
    },
    {
      key: 'catalog', label: 'Catalog', isGroup: true,
      tabs: [
        { value: 'products', canShow: true },
        { value: 'bulk', canShow: true },
        { value: 'catalog-health', canShow: isAdmin },
      ],
    },
    {
      key: 'system', label: 'System', isGroup: true,
      tabs: [
        { value: 'whatsapp', canShow: isAdmin },
        { value: 'errors', canShow: isAdmin },
        { value: 'settings', canShow: isAdmin },
      ],
    },
    {
      key: 'collections', label: 'Collections',
      tabs: [{ value: 'collections', canShow: isAdmin || isManager }],
    },
  ];

  // Reverse map — active pill derived from the currently-active tab so
  // deep links (?tab=settings) automatically land on the right pill.
  const TAB_TO_PILL = PILLS.reduce((acc, p) => {
    p.tabs.forEach((t) => { acc[t.value] = p.key; });
    return acc;
  }, {});
  const activePill = TAB_TO_PILL[activeTab] || 'catalog';

  // Pills with zero visible tabs for this role are hidden entirely so
  // e.g. a manager doesn't see an empty "System" pill.
  const visiblePills = PILLS.filter((p) => p.tabs.some((t) => t.canShow));

  const handlePillClick = (pillKey) => {
    if (pillKey === activePill) return;
    const p = PILLS.find((x) => x.key === pillKey);
    const first = p && p.tabs.find((t) => t.canShow);
    if (first) handleTabChange(first.value);
  };

  const activePillConfig = PILLS.find((p) => p.key === activePill);
  const showSubRow = !!activePillConfig?.isGroup;

  // Level-2 (sub-tab row for Catalog/System groups) styling.
  const L2_CLS =
    'text-sm data-[state=active]:bg-[#556B2F] data-[state=active]:text-white whitespace-nowrap';

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header — title + actions. Stacks vertically on mobile so the action
          buttons get full width and don't sit awkwardly next to the title.
          (iter89 mobile pass.) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-[#1A1C16]">Admin Panel</h1>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate('/stock')}
            className="bg-[#556B2F] hover:bg-[#465926] text-white"
            data-testid="stock-management-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            Stock
          </Button>
          <Button
            onClick={() => navigate('/purchase-orders')}
            className="bg-[#556B2F] hover:bg-[#465926] text-white"
            data-testid="purchase-orders-btn"
          >
            <Package className="w-4 h-4 mr-2" />
            Purchase Orders
          </Button>
          <Button
            onClick={() => navigate('/ai-import')}
            variant="outline"
            className="border-[#556B2F] text-[#556B2F] hover:bg-[#556B2F]/10"
            data-testid="ai-pdf-import-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI PDF Import
          </Button>
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="border-[#E2E6D9] text-[#5C6150] hover:bg-[#556B2F]/8"
                  data-testid="admin-more-btn"
                  aria-label="More admin actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52" data-testid="admin-more-menu">
                <DropdownMenuItem onClick={() => setShowStorage(true)} data-testid="storage-btn">
                  <Database className="w-4 h-4 mr-2" /> Storage
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDownloadPendingReport}
                  data-testid="download-pending-report-btn"
                  title="Pending quotations by salesperson, no activity for 3+ days"
                >
                  <FileText className="w-4 h-4 mr-2" /> Pending Report
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDownloadRolesGuide}
                  data-testid="download-roles-guide-btn"
                  title="Download a PDF guide listing every role and its features — share with your staff team"
                >
                  <FileText className="w-4 h-4 mr-2" /> Roles Guide
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Storage modal — admin only */}
      <Dialog open={showStorage} onOpenChange={setShowStorage}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-[#556B2F]" /> Storage Usage
            </DialogTitle>
          </DialogHeader>
          {showStorage && <StorageStatsCard />}
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        {/* ── Level 1: 8 flat pills. Scrolls horizontally on narrow
            viewports so nothing gets clipped. The active pill uses the
            same olive fill as the sub-tabs. ─────────────────────── */}
        <div className="-mx-2 sm:mx-0 mb-2 overflow-x-auto scrollbar-thin">
          <div className="inline-flex gap-1 p-1 rounded-lg bg-[#F2F4EF] border border-[#E2E6D9] w-max min-w-full" data-testid="admin-pill-nav">
            {visiblePills.map((p) => {
              const isActive = activePill === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handlePillClick(p.key)}
                  className={`px-4 h-8 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-[#556B2F] text-white'
                      : 'text-[#5C6150] hover:bg-[#556B2F]/8'
                  }`}
                  data-testid={`admin-pill-${p.key}`}
                  aria-pressed={isActive}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Level 2: sub-tab strip ONLY for group pills (Catalog,
            System). Direct pills (Analytics, Staff, Today, Walk-ins,
            Leads, Collections) skip this row entirely. ─────────── */}
        {showSubRow && (
          <div className="-mx-2 sm:mx-0 mb-4 overflow-x-auto scrollbar-thin">
            <TabsList className="bg-[#F2F4EF] border border-[#E2E6D9] inline-flex flex-wrap h-auto gap-1 w-max min-w-full py-1">
              {activePill === 'catalog' && (
                <>
                  <TabsTrigger value="products" className={L2_CLS}>
                    Products
                  </TabsTrigger>
                  <TabsTrigger value="bulk" className={L2_CLS}>
                    Bulk Upload
                  </TabsTrigger>
                  {isAdmin && (
                    <TabsTrigger value="catalog-health" className={L2_CLS} data-testid="admin-tab-catalog-health">
                      Catalog Health
                    </TabsTrigger>
                  )}
                </>
              )}
              {activePill === 'system' && (
                <>
                  {isAdmin && (
                    <TabsTrigger value="whatsapp" className={L2_CLS} data-testid="admin-tab-whatsapp">
                      <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
                      <span className="ml-1 text-[8px] font-bold bg-amber-100 text-amber-900 px-1 py-0.5 rounded">BETA</span>
                    </TabsTrigger>
                  )}
                  {isAdmin && (
                    <TabsTrigger value="errors" className={L2_CLS} data-testid="admin-tab-errors">
                      Errors
                    </TabsTrigger>
                  )}
                  {isAdmin && (
                    <TabsTrigger value="settings" className={L2_CLS} data-testid="admin-tab-settings">
                      PDF / T&amp;C
                    </TabsTrigger>
                  )}
                </>
              )}
            </TabsList>
          </div>
        )}

        <TabsContent value="products"><ProductManagement /></TabsContent>
        {(isAdmin || isManager) && (
          <TabsContent value="today"><TodayDashboardPanel /></TabsContent>
        )}
        <TabsContent value="bulk"><BulkUpload /></TabsContent>
        {(isAdmin || isManager) && (
          <TabsContent value="staff"><UserManagement /></TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="analytics"><AnalyticsDashboard /></TabsContent>
        )}
        {(isAdmin || isManager) && (
          <TabsContent value="reports"><ReportsPanel /></TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="whatsapp"><WhatsAppAdminPanel /></TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="catalog-health"><CatalogHealthPage /></TabsContent>
        )}
        {(isAdmin || isManager) && (
          <TabsContent value="walkins"><WalkinRegisterPanel /></TabsContent>
        )}
        {(isAdmin || isManager) && (
          <TabsContent value="leads"><LeadRegisterPanel /></TabsContent>
        )}
        {(isAdmin || isManager) && (
          <TabsContent value="complaints"><ComplaintsPanel /></TabsContent>
        )}
        {(isAdmin || isManager) && (
          <TabsContent value="collections"><CollectionsAdmin /></TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="errors"><ClientErrorsPanel /></TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="settings"><SettingsManagement /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}
